import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useSite, wa } from "@/context/ContentContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";

export default function Footer() {
  const D = useSite();
  const site = D.site;
  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden bg-gradient-to-b from-charcoal-900 to-charcoal-950 text-white"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/3 h-80 w-[42rem] rounded-full bg-gold/10 blur-[130px]"
      />
      <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-20 md:px-12 md:pt-28">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <Link to="/" data-testid="footer-logo" aria-label="Ada Kız Öğrenci Yurdu ana sayfa" className="inline-block">
              <img
                src="/assets/logo-light.png"
                alt="Ada Kız Öğrenci Yurdu"
                className="h-20 w-auto transition-opacity duration-300 hover:opacity-85"
              />
            </Link>
            <p className="mt-7 max-w-sm text-sm leading-relaxed text-white/55">{D.footer.description}</p>
            <div className="mt-8 flex gap-3">
              <a
                data-testid="footer-instagram"
                href={site.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:border-gold/60 hover:text-gold-light"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a
                data-testid="footer-whatsapp"
                href={wa(site)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:border-gold/60 hover:text-gold-light"
              >
                <WhatsAppIcon className="h-4 w-4" />
              </a>
              <a
                data-testid="footer-phone"
                href={`tel:${site.phone.replace(/\s/g, "")}`}
                aria-label="Telefon"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:border-gold/60 hover:text-gold-light"
              >
                <Phone className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h3 className="font-accent text-xs uppercase tracking-[0.3em] text-gold-light">Keşfet</h3>
            <ul className="mt-6 space-y-3.5">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.id}`}
                    className="text-sm text-white/55 transition-colors duration-300 hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="font-accent text-xs uppercase tracking-[0.3em] text-gold-light">İletişim</h3>
            <ul className="mt-6 space-y-5 text-sm text-white/55">
              <li className="flex gap-3.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" strokeWidth={1.5} />
                <span>{site.address}</span>
              </li>
              <li className="flex gap-3.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" strokeWidth={1.5} />
                <span className="flex flex-col gap-1.5">
                  <a href={`tel:${site.phoneTel}`} className="transition-colors duration-300 hover:text-white">
                    {site.phone} <span className="text-white/35">(Mobil & WhatsApp)</span>
                  </a>
                  <a href={`tel:${site.phone2Tel}`} className="transition-colors duration-300 hover:text-white">
                    {site.phone2} <span className="text-white/35">(Sabit Hat)</span>
                  </a>
                </span>
              </li>
              <li className="flex gap-3.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" strokeWidth={1.5} />
                <a href={`mailto:${site.email}`} className="transition-colors duration-300 hover:text-white">
                  {site.email}
                </a>
              </li>
            </ul>
            <p className="mt-8 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-xs leading-relaxed text-white/45">
              {D.footer.note}
            </p>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/35 md:flex-row">
          <p>© 2026 {site.fullName}. Tüm hakları saklıdır.</p>
          <p className="font-accent uppercase tracking-[0.25em]">{D.footer.bottomTagline}</p>
        </div>
      </div>
    </footer>
  );
}
